import { useMutation } from '@apollo/client';
import { Box, Typography } from '@mui/material';
import {
    saveLabAppBannerSettingsTemplate,
    saveClinicAppBannerSettingsTemplate,
    saveAuthAppBannerSettingsTemplate,
} from 'apollo/templates/denthub-settings';
import { AppBannerSetting } from 'models/denthub-settings';
import React, { FC, useEffect, useState } from 'react';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useTranslation } from 'react-i18next';
import BannerForm from './banner-form.component';

type AppBannerSettingsProps = {
    _id: string;
    labAppBannerSettings: AppBannerSetting;
    clinicAppBannerSettings: AppBannerSetting;
    authAppBannerSettings: AppBannerSetting;
    onSettingsSaved: () => void;
};

const AppBannerSettings: FC<AppBannerSettingsProps> = ({
    _id,
    labAppBannerSettings,
    clinicAppBannerSettings,
    authAppBannerSettings,
    onSettingsSaved,
}: AppBannerSettingsProps) => {
    const { t: translate } = useTranslation(['app-settings']);
    const { showBoundary } = useDenthubErrorBoundary();
    const [labBanner, setLabBanner] = useState<AppBannerSetting>({} as AppBannerSetting);
    const [clinicBanner, setClinicBanner] = useState<AppBannerSetting>({} as AppBannerSetting);
    const [authAppBanner, setAuthAppBanner] = useState<AppBannerSetting>({} as AppBannerSetting);

    const [saveLabBanner] = useMutation(saveLabAppBannerSettingsTemplate, {
        onCompleted: onSettingsSaved,
        onError: showBoundary,
    });

    const [saveClinicBanner] = useMutation(saveClinicAppBannerSettingsTemplate, {
        onCompleted: onSettingsSaved,
        onError: showBoundary,
    });

    const [saveAuthAppBanner] = useMutation(saveAuthAppBannerSettingsTemplate, {
        onCompleted: onSettingsSaved,
        onError: showBoundary,
    });

    useEffect(() => {
        setLabBanner(labAppBannerSettings || ({} as AppBannerSetting));
    }, [labAppBannerSettings]);

    useEffect(() => {
        setClinicBanner(clinicAppBannerSettings || ({} as AppBannerSetting));
    }, [clinicAppBannerSettings]);

    useEffect(() => {
        setAuthAppBanner(authAppBannerSettings || ({} as AppBannerSetting));
    }, [authAppBannerSettings]);

    return (
        <Box>
            <Typography variant="h1" gutterBottom>
                {translate('app-settings:APP_BANNER_SETTINGS')}
            </Typography>
            <BannerForm
                sectionTitle={translate('app-settings:AUTH_APP_BANNER')}
                bannerSettings={authAppBanner}
                onToggle={(value) => setAuthAppBanner({ ...authAppBanner, isEnabled: value })}
                onTitleChange={(event) => setAuthAppBanner({ ...authAppBanner, title: event.target.value })}
                onMessageChange={(event) => setAuthAppBanner({ ...authAppBanner, message: event.target.value })}
                onSeveritySelect={(severity) => setAuthAppBanner({ ...authAppBanner, severity })}
                onSave={() => saveAuthAppBanner({ variables: { _id, authAppBannerSetting: authAppBanner } })}
            />
            <BannerForm
                sectionTitle={translate('app-settings:LAB_APP_BANNER')}
                bannerSettings={labBanner}
                onToggle={(value) => setLabBanner({ ...labBanner, isEnabled: value })}
                onTitleChange={(event) => setLabBanner({ ...labBanner, title: event.target.value })}
                onMessageChange={(event) => setLabBanner({ ...labBanner, message: event.target.value })}
                onSeveritySelect={(severity) => setLabBanner({ ...labBanner, severity })}
                onSave={() => saveLabBanner({ variables: { _id, labAppBannerSetting: labBanner } })}
            />
            <BannerForm
                sectionTitle={translate('app-settings:CLINIC_APP_BANNER')}
                bannerSettings={clinicBanner}
                onToggle={(value) => setClinicBanner({ ...clinicBanner, isEnabled: value })}
                onTitleChange={(event) => setClinicBanner({ ...clinicBanner, title: event.target.value })}
                onMessageChange={(event) => setClinicBanner({ ...clinicBanner, message: event.target.value })}
                onSeveritySelect={(severity) => setClinicBanner({ ...clinicBanner, severity })}
                onSave={() => saveClinicBanner({ variables: { _id, clinicAppBannerSetting: clinicBanner } })}
            />
        </Box>
    );
};

export default AppBannerSettings;
